import React from "react";
import { Stack, Text, UnstyledButton, Badge, Group, Box, Divider, Button, ScrollArea } from "@mantine/core";
import { IconFile, IconPlus } from "@tabler/icons-react";
import type { Menu } from "./types";
import { CategoryManager } from "./CategoryManager";

interface Props {
  menu: Menu;
  selectedPageId: string | null;
  onSelectPage: (pageId: string) => void;
  onCreatePage: () => void;
  onRenameCategory: (oldName: string, newName: string) => Promise<void>;
  onReorderCategories: (next: string[]) => Promise<void>;
  mobile?: boolean;
}

export const PageRail: React.FC<Props> = ({
  menu,
  selectedPageId,
  onSelectPage,
  onCreatePage,
  onRenameCategory,
  onReorderCategories,
  mobile = false,
}) => {
  const pages = menu.menuPages || [];

  if (mobile) {
    return (
      <Box
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          background: "var(--mantine-color-gray-0)",
          flexShrink: 0,
        }}
      >
        <ScrollArea scrollbarSize={4}>
          <Group gap={4} p="xs" wrap="nowrap">
            {pages.map((page, idx) => {
              const itemCount = page.menuItems?.length || 0;
              const selected = page.id === selectedPageId;
              return (
                <UnstyledButton
                  key={page.id}
                  onClick={() => onSelectPage(page.id)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 6,
                    background: selected
                      ? "var(--mantine-color-orange-1)"
                      : "transparent",
                    border: selected
                      ? "1px solid var(--mantine-color-orange-4)"
                      : "1px solid var(--mantine-color-gray-3)",
                    transition: "background 100ms",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <Group gap={6} wrap="nowrap">
                    <IconFile size={12} style={{ flexShrink: 0 }} />
                    <Text size="sm">Page {idx + 1}</Text>
                    <Badge size="xs" variant="light" color="gray">
                      {itemCount}
                    </Badge>
                  </Group>
                </UnstyledButton>
              );
            })}
            {pages.length === 0 && (
              <Text size="xs" c="dimmed" px="sm">
                No pages yet
              </Text>
            )}
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconPlus size={12} />}
              onClick={onCreatePage}
              style={{ flexShrink: 0 }}
            >
              Add
            </Button>
          </Group>
        </ScrollArea>
      </Box>
    );
  }

  return (
    <Box
      style={{
        width: 280,
        flexShrink: 0,
        borderRight: "1px solid var(--mantine-color-gray-3)",
        background: "var(--mantine-color-gray-0)",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Stack p="md" gap="lg">
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">
              Pages
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconPlus size={12} />}
              onClick={onCreatePage}
            >
              Add
            </Button>
          </Group>
          <Stack gap={4}>
            {pages.map((page, idx) => {
              const itemCount = page.menuItems?.length || 0;
              const selected = page.id === selectedPageId;
              return (
                <UnstyledButton
                  key={page.id}
                  onClick={() => onSelectPage(page.id)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: selected
                      ? "var(--mantine-color-orange-1)"
                      : "transparent",
                    border: selected
                      ? "1px solid var(--mantine-color-orange-4)"
                      : "1px solid transparent",
                    transition: "background 100ms",
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap={8} wrap="nowrap">
                      <IconFile size={14} style={{ flexShrink: 0 }} />
                      <Text size="sm" lineClamp={1}>
                        Page {idx + 1}
                      </Text>
                    </Group>
                    <Badge size="xs" variant="light" color="gray">
                      {itemCount}
                    </Badge>
                  </Group>
                </UnstyledButton>
              );
            })}
            {pages.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="md">
                No pages yet
              </Text>
            )}
          </Stack>
        </div>

        <Divider />

        <CategoryManager
          menu={menu}
          onRename={onRenameCategory}
          onReorder={onReorderCategories}
        />
      </Stack>
    </Box>
  );
};
